import React, { useState, useEffect } from 'react';
import { SlideOverDrawer } from '@/components/ui/SlideOverDrawer';
import { User, UserActivityEntry } from '@/types/domain/user';
import { USER_STATUS_CONFIG, ROLE_METADATA } from '@/config/user-management.config';
import { formatInr } from '@/lib/utils';
import { mockUserManagementService } from '@/services/mock/MockUserManagementService';
import {
  User as UserIcon,
  Building2,
  Shield,
  Clock,
  KeyRound,
  FileText,
  CheckCircle2,
  XCircle,
  Mail,
  Phone,
  Calendar,
  Lock,
  Cpu,
  Layers,
} from 'lucide-react';

interface UserDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
}

type DetailTab = 'PROFILE' | 'ORGANIZATION' | 'ACCESS' | 'ACCOUNT' | 'SECURITY' | 'ACTIVITY' | 'AUDIT';

export const UserDetailDrawer: React.FC<UserDetailDrawerProps> = ({
  isOpen,
  onClose,
  user,
}) => {
  const [activeTab, setActiveTab] = useState<DetailTab>('PROFILE');
  const [activities, setActivities] = useState<UserActivityEntry[]>([]);
  const [isLoadingActivity, setIsLoadingActivity] = useState(false);

  useEffect(() => {
    if (user && isOpen) {
      setIsLoadingActivity(true);
      mockUserManagementService
        .getUserActivity(user.id)
        .then((data) => setActivities(data))
        .finally(() => setIsLoadingActivity(false));
    }
  }, [user, isOpen]);

  if (!user) return null;

  const statusMeta = USER_STATUS_CONFIG[user.status] || USER_STATUS_CONFIG.ACTIVE;
  const roleMeta = ROLE_METADATA[user.role] || ROLE_METADATA.EMPLOYEE;

  const tabs: { id: DetailTab; label: string; icon: React.ElementType }[] = [
    { id: 'PROFILE', label: 'Identity Profile', icon: UserIcon },
    { id: 'ORGANIZATION', label: 'Organization & Plant', icon: Building2 },
    { id: 'ACCESS', label: 'Role & Scope', icon: Shield },
    { id: 'ACCOUNT', label: 'Lifecycle Status', icon: Clock },
    { id: 'SECURITY', label: 'Security & Auth', icon: KeyRound },
    { id: 'ACTIVITY', label: 'Activity Timeline', icon: Layers },
    { id: 'AUDIT', label: 'Audit Ledger', icon: FileText },
  ];

  return (
    <SlideOverDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={`Identity Dossier — ${user.employeeId}`}
      subtitle={`${user.name} • ${roleMeta.title}`}
    >
      <div className="space-y-4 font-poppins text-xs pb-4">
        {/* User Quick Header Banner */}
        <div className="rounded-2xl border border-border/80 bg-card/90 p-4 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-2xl bg-gradient-to-tr from-primary/20 via-primary/10 to-transparent flex items-center justify-center text-primary font-black text-lg border border-primary/20 shrink-0">
                {user.name.slice(0, 2).toUpperCase()}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-extrabold text-foreground tracking-tight">
                    {user.name}
                  </h3>
                  {user.isSystemUser && (
                    <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-md bg-secondary text-secondary-foreground border">
                      System Managed
                    </span>
                  )}
                </div>
                <div className="text-xs text-foreground-secondary mt-0.5 flex flex-wrap items-center gap-2">
                  <span className="font-mono font-semibold">{user.employeeId}</span>
                  <span>•</span>
                  <span>{user.designation || roleMeta.title}</span>
                  <span>•</span>
                  <span>{user.departmentName}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <div
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${statusMeta.bgColor} ${statusMeta.textColor}`}
              >
                <span className={`h-2 w-2 rounded-full ${statusMeta.dotColor}`} />
                <span>{statusMeta.label}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1 border-b border-border/60 custom-scrollbar">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isCurrent = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl font-bold whitespace-nowrap transition-all cursor-pointer ${
                  isCurrent
                    ? 'bg-primary text-primary-foreground shadow-xs'
                    : 'text-foreground-secondary hover:text-foreground hover:bg-muted/60'
                }`}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab 1: Identity Profile */}
        {activeTab === 'PROFILE' && (
          <div className="space-y-3 animate-in fade-in duration-200">
            <div className="rounded-2xl border border-border/70 bg-card p-4 space-y-3">
              <h4 className="text-xs font-black uppercase tracking-wider text-foreground-muted">
                Personal Identity Attributes
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium block">Full Legal Name</span>
                  <span className="font-bold text-foreground text-sm">{user.name}</span>
                </div>
                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium block">Corporate Employee ID</span>
                  <span className="font-mono font-bold text-foreground text-sm">{user.employeeId}</span>
                </div>
                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium flex items-center gap-1">
                    <Mail className="h-3.5 w-3.5" /> Corporate Email
                  </span>
                  <span className="font-bold text-foreground text-sm select-all">{user.email}</span>
                </div>
                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium flex items-center gap-1">
                    <Phone className="h-3.5 w-3.5" /> Direct Contact Phone
                  </span>
                  <span className="font-bold text-foreground text-sm">{user.phone || '+91 98000 00000'}</span>
                </div>
                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium block">Corporate Designation</span>
                  <span className="font-bold text-foreground">{user.designation || 'Requisitioner'}</span>
                </div>
                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium block">Primary Business Unit</span>
                  <span className="font-bold text-foreground">{user.businessUnit}</span>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-border/70 bg-card p-4 space-y-2">
              <h4 className="text-xs font-black uppercase tracking-wider text-foreground-muted">
                System Identifiers
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-mono">
                <div className="bg-muted/30 p-2 rounded-xl border border-border/50">
                  <span className="text-foreground-secondary block text-[10px]">Internal UUID</span>
                  <span className="font-bold text-foreground break-all">{user.id}</span>
                </div>
                <div className="bg-muted/30 p-2 rounded-xl border border-border/50">
                  <span className="text-foreground-secondary block text-[10px]">Company Code</span>
                  <span className="font-bold text-foreground">{user.companyCode}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Organization & Plant */}
        {activeTab === 'ORGANIZATION' && (
          <div className="space-y-3 animate-in fade-in duration-200">
            <div className="rounded-2xl border border-border/70 bg-card p-4 space-y-3">
              <h4 className="text-xs font-black uppercase tracking-wider text-foreground-muted">
                Organizational Hierarchy
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium block">Department Name</span>
                  <span className="font-bold text-foreground text-sm">{user.departmentName}</span>
                  <span className="text-[10px] text-foreground-muted font-mono block">Code: {user.departmentCode}</span>
                </div>
                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium block">Assigned Operating Plant</span>
                  <span className="font-bold text-foreground text-sm">{user.plantCode}</span>
                  <span className="text-[10px] text-foreground-muted block">Manufacturing & Warehouse Facility</span>
                </div>
                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium block">Allocated Cost Center</span>
                  <span className="font-mono font-bold text-foreground text-sm">{user.costCenterCode || 'CC-1010'}</span>
                </div>
                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium block">Direct Reporting Manager</span>
                  <span className="font-bold text-foreground text-sm">{user.managerName || 'Marcus Vance'}</span>
                  <span className="text-[10px] text-foreground-muted font-mono block">ID: {user.managerId || 'usr-2'}</span>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-border/70 bg-card p-4 space-y-2">
              <h4 className="text-xs font-black uppercase tracking-wider text-foreground-muted">
                Delegation & Approval Hierarchy
              </h4>
              <p className="text-foreground-secondary leading-relaxed">
                Requests initiated by this employee are routed to <span className="font-bold text-foreground">{user.managerName || 'Marcus Vance'}</span> for initial departmental review. Financial disbursements and POs exceeding authority limits automatically escalate according to the Global Delegation of Authority (DOA) matrix.
              </p>
            </div>
          </div>
        )}

        {/* Tab 3: Role & Scope */}
        {activeTab === 'ACCESS' && (
          <div className="space-y-3 animate-in fade-in duration-200">
            <div className="rounded-2xl border border-border/70 bg-card p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-xs font-black uppercase tracking-wider text-foreground-muted">
                    Assigned Primary Role
                  </h4>
                  <div className="text-base font-extrabold text-foreground mt-1">
                    {roleMeta.title}
                  </div>
                </div>
                <div className="px-3 py-1 rounded-xl bg-primary/10 text-primary font-mono font-bold text-xs border border-primary/20">
                  {user.role}
                </div>
              </div>
              <p className="text-foreground-secondary leading-relaxed text-xs">
                {roleMeta.description}
              </p>
            </div>

            {/* Authority Limit Card */}
            <div className="rounded-2xl border border-border/70 bg-card p-4 space-y-2">
              <span className="text-foreground-secondary font-medium block text-xs">
                Delegation of Authority (DOA) Financial Approval Limit
              </span>
              <div className="text-xl font-extrabold text-emerald-600 dark:text-emerald-400 font-mono">
                {user.approvalAuthorityLimit === 99999999
                  ? 'Unlimited Administrative Authority'
                  : formatInr(user.approvalAuthorityLimit)}
              </div>
              <p className="text-[11px] text-foreground-muted">
                Transactions requiring sign-off above this threshold automatically trigger multi-tier approval chains.
              </p>
            </div>

            {/* Data Scope */}
            <div className="rounded-2xl border border-border/70 bg-card p-4 space-y-3">
              <h4 className="text-xs font-black uppercase tracking-wider text-foreground-muted">
                Data Scope & Authorization Boundary
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                <div className="p-2.5 rounded-xl bg-muted/40 border border-border/50">
                  <span className="text-[10px] text-foreground-secondary block">Scope Ownership Model</span>
                  <span className="font-bold text-foreground uppercase">{user.accessScope.ownership}</span>
                </div>
                <div className="p-2.5 rounded-xl bg-muted/40 border border-border/50">
                  <span className="text-[10px] text-foreground-secondary block">Authorized Plants</span>
                  <span className="font-bold text-foreground">
                    {user.accessScope.plants.join(', ') || user.plantCode}
                  </span>
                </div>
              </div>
              <p className="text-[11px] text-foreground-muted leading-relaxed">
                Access is strictly restricted to designated organizational units. Users cannot view purchase orders or vendor quotations outside their defined plant and department boundaries.
              </p>
            </div>

            {/* Functional Permissions */}
            <div className="rounded-2xl border border-border/70 bg-card p-4 space-y-2">
              <h4 className="text-xs font-black uppercase tracking-wider text-foreground-muted">
                Active System Permissions ({user.permissions.length})
              </h4>
              <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto custom-scrollbar p-1">
                {user.permissions.map((perm) => (
                  <span
                    key={perm}
                    className="font-mono text-[10px] font-bold px-2 py-0.5 rounded-lg bg-muted border border-border text-foreground-secondary"
                  >
                    {perm}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Tab 4: Lifecycle Status */}
        {activeTab === 'ACCOUNT' && (
          <div className="space-y-3 animate-in fade-in duration-200">
            <div className="rounded-2xl border border-border/70 bg-card p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-black uppercase tracking-wider text-foreground-muted">
                  Current Lifecycle State
                </span>
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${statusMeta.bgColor} ${statusMeta.textColor}`}
                >
                  <span className={`h-2 w-2 rounded-full ${statusMeta.dotColor}`} />
                  <span>{statusMeta.label}</span>
                </span>
              </div>
              <p className="text-xs text-foreground-secondary leading-relaxed">
                {statusMeta.description}
              </p>
              {user.statusReason && (
                <div className="p-2.5 rounded-xl bg-muted/50 border border-border/60 text-xs">
                  <span className="font-bold text-foreground block">Admin Note / Reason:</span>
                  <span className="text-foreground-secondary italic">{user.statusReason}</span>
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-border/70 bg-card p-4 space-y-3">
              <h4 className="text-xs font-black uppercase tracking-wider text-foreground-muted">
                Key Lifecycle Timestamps
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" /> Account Provisioned
                  </span>
                  <span className="font-mono font-bold text-foreground">
                    {user.createdAt ? new Date(user.createdAt).toLocaleString() : 'N/A'}
                  </span>
                </div>
                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium flex items-center gap-1">
                    <Mail className="h-3.5 w-3.5" /> Invitation Dispatched
                  </span>
                  <span className="font-mono font-bold text-foreground">
                    {user.invitationSentAt ? new Date(user.invitationSentAt).toLocaleString() : 'N/A'}
                  </span>
                </div>
                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" /> Last Successful Login
                  </span>
                  <span className="font-mono font-bold text-foreground">
                    {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Never Authenticated'}
                  </span>
                </div>
                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" /> Record Last Modified
                  </span>
                  <span className="font-mono font-bold text-foreground">
                    {user.updatedAt ? new Date(user.updatedAt).toLocaleString() : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab 5: Security & Auth */}
        {activeTab === 'SECURITY' && (
          <div className="space-y-3 animate-in fade-in duration-200">
            <div className="rounded-2xl border border-border/70 bg-card p-4 space-y-3">
              <h4 className="text-xs font-black uppercase tracking-wider text-foreground-muted">
                Authentication & Identity Security
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-3 rounded-xl border border-border/60 bg-muted/20 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-foreground">Enterprise SSO</span>
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  </div>
                  <span className="text-[11px] text-foreground-secondary block">
                    Microsoft Entra ID / SAP NetWeaver Federation active.
                  </span>
                </div>

                <div className="p-3 rounded-xl border border-border/60 bg-muted/20 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-foreground">Two-Factor Authentication</span>
                    {user.mfaEnabled ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-amber-500" />
                    )}
                  </div>
                  <span className="text-[11px] text-foreground-secondary block">
                    {user.mfaEnabled
                      ? 'Hardware token / TOTP Authenticator enforced.'
                      : 'MFA not required for current privilege level.'}
                  </span>
                </div>

                <div className="p-3 rounded-xl border border-border/60 bg-muted/20 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-foreground">Account Lock State</span>
                    {user.status === 'LOCKED' ? (
                      <Lock className="h-4 w-4 text-rose-600" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    )}
                  </div>
                  <span className="text-[11px] text-foreground-secondary block">
                    {user.status === 'LOCKED'
                      ? 'Account currently locked due to failed attempts.'
                      : 'Normal authentication standing.'}
                  </span>
                </div>

                <div className="p-3 rounded-xl border border-border/60 bg-muted/20 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-foreground">Password Governance</span>
                    <Cpu className="h-4 w-4 text-primary" />
                  </div>
                  <span className="text-[11px] text-foreground-secondary block">
                    Enterprise TLS 1.3 encrypted. Admin cannot view user passwords.
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab 6: Activity Timeline */}
        {activeTab === 'ACTIVITY' && (
          <div className="space-y-3 animate-in fade-in duration-200">
            <div className="rounded-2xl border border-border/70 bg-card p-4 space-y-3">
              <h4 className="text-xs font-black uppercase tracking-wider text-foreground-muted">
                User Activity Stream ({activities.length})
              </h4>
              {isLoadingActivity ? (
                <div className="py-6 text-center text-foreground-muted">Loading audit history...</div>
              ) : activities.length === 0 ? (
                <div className="py-6 text-center text-foreground-muted">No recent activity recorded.</div>
              ) : (
                <div className="relative border-l-2 border-border/70 ml-2 space-y-4 pl-4 py-1">
                  {activities.map((act) => (
                    <div key={act.id} className="relative group">
                      <div className="absolute -left-[23px] top-0.5 h-3 w-3 rounded-full bg-primary ring-4 ring-card" />
                      <div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-bold text-foreground text-xs">{act.action}</span>
                          <span className="text-[10px] text-foreground-muted font-mono">
                            {new Date(act.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <p className="text-[11px] text-foreground-secondary mt-0.5 leading-relaxed">
                          {act.description}
                        </p>
                        <div className="text-[10px] text-foreground-muted mt-1 flex items-center gap-1.5 font-medium">
                          <span>Actor: {act.actor}</span>
                          <span>•</span>
                          <span>{act.actorRole}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab 7: Audit Ledger */}
        {activeTab === 'AUDIT' && (
          <div className="space-y-3 animate-in fade-in duration-200">
            <div className="rounded-2xl border border-border/70 bg-card p-4 space-y-3">
              <h4 className="text-xs font-black uppercase tracking-wider text-foreground-muted">
                Compliance & Security Audit Trail
              </h4>
              <p className="text-foreground-secondary text-xs leading-relaxed">
                All administrative changes to roles, authority limits, deactivations, and security blocks generate immutable, tamper-evident audit records linked to the Enterprise Audit Service.
              </p>

              <div className="p-3 rounded-xl bg-muted/40 border border-border/60 space-y-2 text-xs">
                <div className="flex items-center justify-between font-mono text-[11px]">
                  <span className="font-bold text-foreground">Record UID</span>
                  <span className="text-foreground-secondary">{user.id}</span>
                </div>
                <div className="flex items-center justify-between font-mono text-[11px]">
                  <span className="font-bold text-foreground">Audit Entity Type</span>
                  <span className="text-foreground-secondary">IDENTITY_MASTER_DATA</span>
                </div>
                <div className="flex items-center justify-between font-mono text-[11px]">
                  <span className="font-bold text-foreground">Tamper Protection</span>
                  <span className="text-emerald-600 font-bold">SHA-256 Verified</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </SlideOverDrawer>
  );
};
